/**
 * Configurações da plataforma: honorários por tipo de ação, integrações
 * prontas para ligar (assinatura, WhatsApp, conector de tribunais),
 * funções liberadas, API pública/webhooks e LGPD.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import { useCatalog } from "@/lib/catalog";
import type { FeaturesResponse } from "@/lib/features";
import { confirmar, toast } from "@/ui/feedback";
import { ActionTypeSelect, AreaSelect, Badge, Card, DataTable, ErrorBox, Field, FormGrid, Loading, Modal, useAct } from "@/ui/kit";

// ================================================================ honorários

interface FeeModel {
  id: string; name: string; areaId: string | null; subjectTypeId: string | null; kind: "fixo" | "percentual" | "misto";
  fixedAmount: string | null; entryAmount: string | null; installments: number | null; successPercent: string | null; notes: string | null; active: boolean;
}
const KIND_LABEL = { fixo: "Valor fixo", percentual: "Percentual de êxito", misto: "Fixo + êxito" };

export function FeeSettings(): JSX.Element {
  const { data: cat } = useCatalog();
  const { data, error } = useQuery({ queryKey: ["honorarios"], queryFn: () => api.get<FeeModel[]>("/honorarios") });
  const [edit, setEdit] = useState<Partial<FeeModel> | null>(null);
  const disable = useAct((id: string) => api.del(`/config/honorarios/${id}`), [["honorarios"]]);
  const subjectName = (id: string | null) => cat?.areas.flatMap((a) => a.subjects).find((s) => s.id === id)?.name ?? "—";
  const areaName = (id: string | null) => cat?.areas.find((a) => a.id === id)?.name ?? "Todas";
  return (
    <Card title="Honorários por tipo de ação" actions={<button className="btn small primary" onClick={() => setEdit({ kind: "fixo", installments: 1 })}>Novo modelo</button>}>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Sugestão automática ao gerar o contrato de honorários e ao lançar no Financeiro. Vale o modelo do tipo de ação do caso; sem ele, o da área.
      </div>
      <ErrorBox error={error ?? disable.error} />
      <DataTable rows={data} rowKey={(r) => r.id} empty="Nenhum modelo cadastrado."
        columns={[
          { key: "n", label: "Modelo", render: (r) => <>{r.name}{!r.active && <> <Badge tone="muted">inativo</Badge></>}<div className="muted small">{areaName(r.areaId)} · {subjectName(r.subjectTypeId)}</div></> },
          { key: "k", label: "Forma", width: 160, render: (r) => KIND_LABEL[r.kind] },
          { key: "v", label: "Valores", render: (r) => (
            <span className="small">
              {r.fixedAmount && <>Total {fmtMoney(r.fixedAmount)}</>}
              {r.entryAmount && <> · entrada {fmtMoney(r.entryAmount)}</>}
              {r.installments && r.installments > 1 && <> · {r.installments}x</>}
              {r.successPercent && <> · êxito {Number(r.successPercent).toLocaleString("pt-BR")}%</>}
            </span>
          ) },
          { key: "a", label: "", width: 170, align: "right", render: (r) => (
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn small" onClick={() => setEdit(r)}>Editar</button>
              {r.active && <button className="btn small" onClick={() => void confirmar(`Desativar "${r.name}"?`).then((y) => y && disable.mutate(r.id))}>Desativar</button>}
            </div>
          ) },
        ]} />
      {edit && <FeeModal value={edit} onClose={() => setEdit(null)} />}
    </Card>
  );
}

function FeeModal({ value, onClose }: { value: Partial<FeeModel>; onClose: () => void }): JSX.Element {
  const [f, setF] = useState<Partial<FeeModel>>(value);
  const money = (v: string | null | undefined) => (v ? v.replace(/\./g, "").replace(",", ".").trim() || null : null);
  const save = useAct(() => {
    const body = {
      name: f.name, areaId: f.areaId || null, subjectTypeId: f.subjectTypeId || null, kind: f.kind,
      fixedAmount: money(f.fixedAmount), entryAmount: money(f.entryAmount), installments: f.installments ? Number(f.installments) : null,
      successPercent: f.successPercent ? Number(String(f.successPercent).replace(",", ".")) : null, notes: f.notes || null,
    };
    return f.id ? api.patch(`/config/honorarios/${f.id}`, { ...body, active: f.active ?? true }) : api.post("/config/honorarios", body);
  }, [["honorarios"]], onClose);
  return (
    <Modal title={f.id ? "Editar modelo de honorários" : "Novo modelo de honorários"} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!f.name || save.isPending} onClick={() => save.mutate()}>Salvar</button></>}>
      <FormGrid cols={2}>
        <Field label="Nome" required span={2}><input className="input" value={f.name ?? ""} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Aposentadoria — padrão" /></Field>
        <Field label="Área"><AreaSelect value={f.areaId} onChange={(v) => setF({ ...f, areaId: v, subjectTypeId: null })} allowEmpty /></Field>
        <Field label="Tipo de ação"><ActionTypeSelect areaId={f.areaId} value={f.subjectTypeId} onChange={(v) => setF({ ...f, subjectTypeId: v })} emptyLabel="Qualquer da área" /></Field>
        <Field label="Forma">
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as FeeModel["kind"] })}>
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        {f.kind !== "fixo" && <Field label="Êxito (%)"><input className="input" inputMode="decimal" value={f.successPercent ?? ""} onChange={(e) => setF({ ...f, successPercent: e.target.value })} placeholder="30" /></Field>}
        {f.kind !== "percentual" && <>
          <Field label="Valor total (R$)"><input className="input" inputMode="decimal" value={f.fixedAmount ?? ""} onChange={(e) => setF({ ...f, fixedAmount: e.target.value })} placeholder="5.000,00" /></Field>
          <Field label="Entrada (R$)"><input className="input" inputMode="decimal" value={f.entryAmount ?? ""} onChange={(e) => setF({ ...f, entryAmount: e.target.value })} /></Field>
          <Field label="Parcelas"><input className="input" type="number" min={1} max={120} value={f.installments ?? ""} onChange={(e) => setF({ ...f, installments: e.target.value ? Number(e.target.value) : null })} /></Field>
        </>}
        <Field label="Observações" span={2}><textarea className="input" rows={2} value={f.notes ?? ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      </FormGrid>
      <ErrorBox error={save.error} />
    </Modal>
  );
}

// =============================================================== integrações

interface Endpoint { tribunal: string; sistema: string; grau: "1" | "2"; url: string; avisos: boolean; movimentos: boolean; enabled: boolean }
interface IntegStatus {
  clicksign: { configured: boolean; env: string };
  whatsapp: { configured: boolean; enabled: boolean; fatalTemplate: string; resumoTemplate: string; templates: Array<{ name: string; body: string }> };
  conector: {
    serverEnabled: boolean; credentialSource: string | null; enabled: boolean; cpfConsultante: string; validadeCertificado: string | null; endpoints: Endpoint[];
    lastRun: { startedAt: string; status: string; fetched: number; created: number; error: string | null } | null;
  };
}

function ServerBadge({ ok, label = "credenciais no servidor" }: { ok: boolean; label?: string }): JSX.Element {
  return ok ? <Badge tone="ok">{label}: ok</Badge> : <Badge tone="warn">{label}: faltando</Badge>;
}

export function IntegrationSettings(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["integracoes"], queryFn: () => api.get<IntegStatus>("/config/integracoes") });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return (
    <div className="stack gap-16">
      <Card title="Assinatura eletrônica (Clicksign)" actions={<ServerBadge ok={data.clicksign.configured} />}>
        <div className="small">
          Ambiente: <b>{data.clicksign.env === "producao" ? "produção" : "sandbox (testes)"}</b>. Para ligar: coloque <code>CLICKSIGN_TOKEN</code> (e <code>CLICKSIGN_ENV=producao</code> quando for para valer) no <code>.env</code> do servidor e ative “Assinatura eletrônica” em Funções e API.
          O botão “Assinar” aparece nos PDFs em Documentos; o PDF assinado volta como nova versão.
        </div>
      </Card>
      <WhatsappCard s={data.whatsapp} />
      <ConnectorCard s={data.conector} />
    </div>
  );
}

function WhatsappCard({ s }: { s: IntegStatus["whatsapp"] }): JSX.Element {
  const [f, setF] = useState({ enabled: s.enabled, fatalTemplate: s.fatalTemplate, resumoTemplate: s.resumoTemplate });
  const [phone, setPhone] = useState("");
  const save = useAct(() => api.put("/config/whatsapp", f), [["integracoes"]]);
  const test = useAct(() => api.post("/config/whatsapp/teste", { phone }), [], undefined, "Mensagem de teste enviada");
  const hook = `${window.location.origin.replace(":5173", ":3000")}/api/v1/publico/whatsapp/webhook`;
  return (
    <Card title="WhatsApp (API oficial da Meta)" actions={<ServerBadge ok={s.configured} />}>
      <div className="stack gap-12 small">
        <div>
          Envia avisos de <b>prazo fatal</b> e o <b>resumo do dia</b> para quem cadastrar o número em Minha conta. Precisa, no servidor:
          <code> WHATSAPP_TOKEN</code>, <code>WHATSAPP_PHONE_NUMBER_ID</code> e <code>WHATSAPP_VERIFY_TOKEN</code>; e na Meta, os modelos abaixo aprovados (categoria “Utilidade”, idioma pt_BR).
        </div>
        <DataTable rows={s.templates} rowKey={(t) => t.name} columns={[
          { key: "n", label: "Modelo", width: 170, render: (t) => <code>{t.name}</code> },
          { key: "b", label: "Texto sugerido", render: (t) => t.body },
        ]} />
        <div className="muted">Webhook (status de entrega): <code>{hook}</code> — verificação GET com o WHATSAPP_VERIFY_TOKEN; status em <code>…/publico/whatsapp/&lt;escritório&gt;/webhook</code>.</div>
        <FormGrid cols={3}>
          <Field label="Modelo do prazo fatal"><input className="input" value={f.fatalTemplate} onChange={(e) => setF({ ...f, fatalTemplate: e.target.value })} /></Field>
          <Field label="Modelo do resumo diário"><input className="input" value={f.resumoTemplate} onChange={(e) => setF({ ...f, resumoTemplate: e.target.value })} /></Field>
          <Field label="Envio"><label className="row nowrap"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Ligado</label></Field>
        </FormGrid>
        <div className="row">
          <button className="btn small primary" disabled={save.isPending} onClick={() => save.mutate()}>Salvar</button>
          <span className="spacer" />
          <input className="input" style={{ maxWidth: 180 }} placeholder="Seu celular" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <button className="btn small" disabled={!s.configured || phone.replace(/\D/g, "").length < 10 || test.isPending} onClick={() => test.mutate()}>Enviar teste (hello_world)</button>
        </div>
        <ErrorBox error={save.error ?? test.error} />
      </div>
    </Card>
  );
}

function ConnectorCard({ s }: { s: IntegStatus["conector"] }): JSX.Element {
  const [on, setOn] = useState(s.enabled);
  const save = useAct((enabled: boolean) => api.put("/config/conector-tribunais", { enabled, cpfConsultante: s.cpfConsultante ?? "", validadeCertificado: s.validadeCertificado || null, endpoints: s.endpoints ?? [] }), [["integracoes"]]);
  return (
    <Card title="Conector de tribunais (MNI)" actions={<Badge tone={s.serverEnabled ? "ok" : "muted"}>{s.serverEnabled ? "servidor: ligado" : "servidor: desligado"}</Badge>}>
      <div className="stack gap-12 small">
        <div>
          Consulta <b>avisos pendentes</b> e <b>movimentações</b> direto no PJe dos tribunais (MNI), antes da publicação no DJEN, inclusive em segredo de justiça.
          Nunca abre o teor (para não dar ciência). Tribunais, advogados, senhas e certificados ficam em <a href="/configuracoes?aba=advogados">Advogados e tribunais</a>.
          Só roda com <code>TRIBUNAL_CONNECTOR_ENABLED=true</code> no servidor <b>e</b> ligado aqui.
        </div>
        {s.lastRun && <div className="muted">Última execução: {fmtDateTime(s.lastRun.startedAt)} — {s.lastRun.status}, {s.lastRun.fetched} lido(s), {s.lastRun.created} novo(s){s.lastRun.error ? ` · ${s.lastRun.error}` : ""}</div>}
        <div className="row">
          <label className="row nowrap"><input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} /> Captura ligada</label>
          <button className="btn small primary" disabled={save.isPending || on === s.enabled} onClick={() => save.mutate(on)}>Salvar</button>
        </div>
        <ErrorBox error={save.error} />
      </div>
    </Card>
  );
}

// ============================================================ funções e API

interface KeyRow { id: string; name: string; prefix: string; scopes: string[]; lastUsedAt: string | null; revokedAt: string | null; createdAt: string }
interface HookRow { id: string; name: string; url: string; events: string[]; active: boolean; createdAt: string }
interface Delivery { id: string; subscriptionId: string; eventType: string; status: string; attempts: number; responseCode: number | null; error: string | null; createdAt: string }

export function PlatformSettings(): JSX.Element {
  return <div className="stack gap-16"><FeatureFlags /><ApiKeys /><Webhooks /></div>;
}

function FeatureFlags(): JSX.Element {
  const { data } = useQuery({ queryKey: ["recursos"], queryFn: () => api.get<FeaturesResponse>("/recursos") });
  const save = useAct((v: Record<string, boolean>) => api.put("/config/recursos", v), [["recursos"]]);
  if (!data) return <Loading />;
  return (
    <Card title="Funções liberadas">
      <div className="muted small" style={{ marginBottom: 8 }}>Desligar esconde a função para todos do escritório (os dados continuam guardados).</div>
      <div className="grid cols-2">
        {data.catalog.map((f) => (
          <label key={f.key} className="row nowrap">
            <input type="checkbox" checked={data.values[f.key]} disabled={save.isPending} onChange={(e) => save.mutate({ ...data.values, [f.key]: e.target.checked })} /> {f.label}
          </label>
        ))}
      </div>
      <ErrorBox error={save.error} />
    </Card>
  );
}

function ApiKeys(): JSX.Element {
  const { data } = useQuery({ queryKey: ["api-chaves"], queryFn: () => api.get<{ items: KeyRow[]; scopes: Array<{ key: string; label: string }> }>("/config/api/chaves") });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [shown, setShown] = useState<string | null>(null);
  const create = useAct(() => api.post<{ key: string }>("/config/api/chaves", { name, scopes }), [["api-chaves"]], (r) => { setShown(r.key); setCreating(false); setName(""); setScopes([]); }, "Chave criada");
  const revoke = useAct((id: string) => api.post(`/config/api/chaves/${id}/revogar`), [["api-chaves"]], undefined, "Chave revogada");
  const base = `${window.location.origin.replace(":5173", ":3000")}/api/v1/publico/api/v1`;
  return (
    <Card title="API pública (somente leitura)" actions={<button className="btn small primary" onClick={() => setCreating(true)}>Nova chave</button>}>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Base: <code>{base}</code> — rotas <code>/pessoas</code>, <code>/processos</code>, <code>/processos/:id</code>, <code>/atividades</code>, <code>/financeiro/titulos</code> (parâmetros take, skip, desde). Cabeçalho <code>Authorization: Bearer &lt;chave&gt;</code>. Limite: 60 req/min. Precisa da função “API pública e webhooks” ligada.
      </div>
      {shown && (
        <div className="warn-box">
          <b>Copie agora — não será exibida de novo:</b>
          <div className="row nowrap"><code style={{ wordBreak: "break-all" }}>{shown}</code><button className="btn small" onClick={() => void navigator.clipboard.writeText(shown).then(() => toast.ok("Copiada"))}>Copiar</button><button className="btn small" onClick={() => setShown(null)}>Ok</button></div>
        </div>
      )}
      <DataTable rows={data?.items} rowKey={(k) => k.id} empty="Nenhuma chave."
        columns={[
          { key: "n", label: "Nome", render: (k) => <>{k.name} {k.revokedAt && <Badge tone="muted">revogada</Badge>}<div className="muted small mono">vgk_…_{k.prefix}_…</div></> },
          { key: "s", label: "Escopos", render: (k) => <span className="small">{k.scopes.join(", ")}</span> },
          { key: "u", label: "Último uso", width: 150, render: (k) => (k.lastUsedAt ? fmtDateTime(k.lastUsedAt) : "nunca") },
          { key: "a", label: "", width: 100, align: "right", render: (k) => !k.revokedAt && <button className="btn small" onClick={() => void confirmar(`Revogar a chave "${k.name}"? Integrações que a usam param na hora.`).then((y) => y && revoke.mutate(k.id))}>Revogar</button> },
        ]} />
      {creating && (
        <Modal title="Nova chave de API" onClose={() => setCreating(false)}
          footer={<><button className="btn" onClick={() => setCreating(false)}>Cancelar</button><button className="btn primary" disabled={name.trim().length < 2 || !scopes.length || create.isPending} onClick={() => create.mutate()}>Criar</button></>}>
          <FormGrid cols={1}>
            <Field label="Nome (quem vai usar)" required><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Planilha do financeiro" /></Field>
            <Field label="Pode ler">
              <div className="chips">{(data?.scopes ?? []).map((s) => (
                <button key={s.key} type="button" className={`chip ${scopes.includes(s.key) ? "on" : ""}`} onClick={() => setScopes(scopes.includes(s.key) ? scopes.filter((x) => x !== s.key) : [...scopes, s.key])}>{s.label}</button>
              ))}</div>
            </Field>
          </FormGrid>
          <ErrorBox error={create.error} />
        </Modal>
      )}
      <ErrorBox error={revoke.error} />
    </Card>
  );
}

function Webhooks(): JSX.Element {
  const { data } = useQuery({ queryKey: ["webhooks"], queryFn: () => api.get<{ items: HookRow[]; recent: Delivery[]; events: string[] }>("/config/webhooks"), refetchInterval: 30_000 });
  const [edit, setEdit] = useState<Partial<HookRow> | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const save = useAct(() => (edit?.id
    ? api.patch(`/config/webhooks/${edit.id}`, { name: edit.name, url: edit.url, events: edit.events, active: edit.active })
    : api.post<{ secret: string }>("/config/webhooks", { name: edit?.name, url: edit?.url, events: edit?.events ?? [], active: true })),
  [["webhooks"]], (r) => { const s = (r as { secret?: string } | undefined)?.secret; if (s) setSecret(s); setEdit(null); });
  const del = useAct((id: string) => api.del(`/config/webhooks/${id}`), [["webhooks"]], undefined, "Webhook removido");
  const test = useAct((id: string) => api.post(`/config/webhooks/${id}/testar`), [["webhooks"]], undefined, "Teste enfileirado (sai em até 1 min)");
  const hookName = (id: string) => data?.items.find((h) => h.id === id)?.name ?? "—";
  return (
    <Card title="Webhooks" actions={<button className="btn small primary" onClick={() => setEdit({ events: [] })}>Novo webhook</button>}>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Envia um POST JSON para a sua URL quando algo acontece. Assinatura HMAC-SHA256 do corpo no cabeçalho <code>X-VG-Signature: sha256=…</code> (com o segredo); até 8 novas tentativas com espera crescente.
      </div>
      {secret && <div className="warn-box"><b>Segredo (copie agora):</b> <code>{secret}</code> <button className="btn small" onClick={() => setSecret(null)}>Ok</button></div>}
      <DataTable rows={data?.items} rowKey={(h) => h.id} empty="Nenhum webhook."
        columns={[
          { key: "n", label: "Nome", render: (h) => <>{h.name} {!h.active && <Badge tone="muted">pausado</Badge>}<div className="muted small">{h.url}</div></> },
          { key: "e", label: "Eventos", render: (h) => <span className="small">{h.events.length} evento(s)</span> },
          { key: "a", label: "", width: 230, align: "right", render: (h) => (
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn small" onClick={() => test.mutate(h.id)}>Testar</button>
              <button className="btn small" onClick={() => setEdit(h)}>Editar</button>
              <button className="btn small" onClick={() => void confirmar(`Remover "${h.name}"?`).then((y) => y && del.mutate(h.id))}>Remover</button>
            </div>
          ) },
        ]} />
      {(data?.recent.length ?? 0) > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="small">Últimos envios</summary>
          <DataTable rows={data!.recent} rowKey={(d) => d.id} columns={[
            { key: "w", label: "Quando", width: 140, render: (d) => fmtDateTime(d.createdAt) },
            { key: "h", label: "Webhook", render: (d) => hookName(d.subscriptionId) },
            { key: "e", label: "Evento", render: (d) => <code className="small">{d.eventType}</code> },
            { key: "s", label: "Situação", width: 190, render: (d) => <><Badge tone={d.status === "entregue" ? "ok" : d.status === "falhou" ? "bad" : "info"}>{d.status}</Badge> <span className="muted small">{d.responseCode ?? ""} {d.attempts > 1 ? `· ${d.attempts} tent.` : ""}</span>{d.error && <div className="muted small">{d.error}</div>}</> },
          ]} />
        </details>
      )}
      {edit && (
        <Modal title={edit.id ? "Editar webhook" : "Novo webhook"} onClose={() => setEdit(null)} wide
          footer={<><button className="btn" onClick={() => setEdit(null)}>Cancelar</button><button className="btn primary" disabled={!edit.name || !edit.url || !edit.events?.length || save.isPending} onClick={() => save.mutate()}>Salvar</button></>}>
          <FormGrid cols={2}>
            <Field label="Nome" required><input className="input" value={edit.name ?? ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="URL (https)" required><input className="input" value={edit.url ?? ""} onChange={(e) => setEdit({ ...edit, url: e.target.value })} placeholder="https://…" /></Field>
            {edit.id && <Field label="Situação"><label className="row nowrap"><input type="checkbox" checked={edit.active ?? true} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Ativo</label></Field>}
            <Field label="Eventos" span={2}>
              <div className="chips">{(data?.events ?? []).map((ev) => {
                const on = edit.events?.includes(ev);
                return <button key={ev} type="button" className={`chip ${on ? "on" : ""}`} onClick={() => setEdit({ ...edit, events: on ? edit.events!.filter((x) => x !== ev) : [...(edit.events ?? []), ev] })}>{ev.replace(/\.v1$/, "")}</button>;
              })}</div>
            </Field>
          </FormGrid>
          <ErrorBox error={save.error} />
        </Modal>
      )}
      <ErrorBox error={del.error ?? test.error} />
    </Card>
  );
}

// ===================================================================== LGPD

interface LgpdInfo { retencaoAnos: number; candidates: Array<{ id: string; name: string; updatedAt: string }>; requests: Array<{ id: string; personId: string; kind: string; reason: string; createdAt: string }> }

export function LgpdSettings(): JSX.Element {
  const { data } = useQuery({ queryKey: ["lgpd"], queryFn: () => api.get<LgpdInfo>("/config/lgpd") });
  const [years, setYears] = useState<number | null>(null);
  const save = useAct(() => api.put("/config/lgpd", { retencaoAnos: years }), [["lgpd"]], () => setYears(null));
  if (!data) return <Loading />;
  return (
    <Card title="LGPD — retenção e pedidos de titulares">
      <div className="stack gap-12 small">
        <div>
          Exportar dados e anonimizar ficam na ficha da pessoa (botão “LGPD”). A anonimização é bloqueada enquanto houver caso, processo ou título em aberto (dever de guarda).
        </div>
        <div className="row">
          <span>Guardar cadastros sem vínculo por</span>
          <input className="input" type="number" min={1} max={30} style={{ width: 70 }} value={years ?? data.retencaoAnos} onChange={(e) => setYears(Number(e.target.value))} />
          <span>anos</span>
          <button className="btn small primary" disabled={years === null || save.isPending} onClick={() => save.mutate()}>Salvar</button>
        </div>
        <div><b>Candidatos à anonimização</b> (sem caso/processo e sem alteração há mais de {data.retencaoAnos} anos): {data.candidates.length}</div>
        {data.candidates.length > 0 && (
          <DataTable rows={data.candidates.slice(0, 50)} rowKey={(c) => c.id} onRowClick={(c) => { window.location.href = `/pessoas/${c.id}`; }}
            columns={[{ key: "n", label: "Pessoa", render: (c) => c.name }, { key: "u", label: "Última alteração", width: 150, render: (c) => fmtDate(c.updatedAt) }]} />
        )}
        <div><b>Pedidos registrados</b></div>
        <DataTable rows={data.requests} rowKey={(r) => r.id} empty="Nenhum pedido."
          columns={[
            { key: "w", label: "Quando", width: 140, render: (r) => fmtDateTime(r.createdAt) },
            { key: "k", label: "Tipo", width: 120, render: (r) => <Badge tone={r.kind === "anonimizacao" ? "warn" : "info"}>{r.kind}</Badge> },
            { key: "r", label: "Motivo", render: (r) => r.reason },
          ]} />
        <ErrorBox error={save.error} />
      </div>
    </Card>
  );
}

/** Botões LGPD na ficha da pessoa (administrador). */
export function PersonLgpd({ personId, name }: { personId: string; name: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const anon = useAct(() => api.post(`/pessoas/${personId}/anonimizar`, { reason }), [["pessoa", personId], ["pessoas"]], () => setOpen(false), "Pessoa anonimizada");
  const exportData = async () => {
    try {
      const data = await api.get<unknown>(`/pessoas/${personId}/dados`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = `dados-${name.replace(/[^\w]+/g, "-").toLowerCase()}.json`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error((e as Error).message); }
  };
  return (
    <>
      <button className="btn small" onClick={() => void exportData()} title="Exporta os dados pessoais (pedido do titular)">LGPD: exportar</button>
      <button className="btn small" onClick={() => setOpen(true)}>LGPD: anonimizar</button>
      {open && (
        <Modal title={`Anonimizar ${name}`} onClose={() => setOpen(false)}
          footer={<><button className="btn" onClick={() => setOpen(false)}>Cancelar</button><button className="btn danger" disabled={reason.trim().length < 10 || anon.isPending} onClick={() => anon.mutate()}>Anonimizar (irreversível)</button></>}>
          <div className="stack gap-8 small">
            <div>Apaga nome, documentos, contatos, endereços e observações. Casos e documentos já encerrados continuam, sem identificação. <b>Não dá para desfazer.</b></div>
            <Field label="Motivo (fica registrado)" required><textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: pedido do titular por e-mail em 10/09/2026" /></Field>
            <ErrorBox error={anon.error} />
          </div>
        </Modal>
      )}
    </>
  );
}
